import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { RefreshCw, Copy, MousePointerClick, CalendarCheck, Euro, Wallet } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-white border border-cream-300 p-5">
    <div className="flex items-center gap-2 text-sand-500 text-xs font-semibold uppercase tracking-wide">
      <Icon className="h-4 w-4 text-terra" /> {label}
    </div>
    <p className="mt-2 font-heading font-bold text-3xl text-sand-800">{value}</p>
    {sub && <p className="mt-1 text-xs text-sand-500">{sub}</p>}
  </div>
);

const eur = (v) => `€ ${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`;

export default function CreatorStats() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const token = params.get("key") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/affiliate/creator-stats/${encodeURIComponent(code)}`, { params: { token } })
      .then((res) => { setData(res.data); setError(null); })
      .catch(() => setError("Dieser Zugangslink ist ungültig oder abgelaufen."));
  }, [code, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center p-6">
        <p data-testid="creator-stats-error" className="text-sand-700">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-cream-100 flex items-center justify-center text-sand-500">Lade deine Zahlen …</div>;
  }

  const maxClicks = Math.max(1, ...data.verlauf.map((d) => d.clicks));

  return (
    <div className="min-h-screen bg-cream-100 pb-16">
      <div className="max-w-3xl mx-auto px-5 pt-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-terra">Dickies-Campus · Creator-Dashboard</p>
        <h1 className="mt-1 font-heading font-bold text-3xl text-sand-800">Hallo {data.name} 👋</h1>
        <p className="mt-1 text-sm text-sand-600">
          Deine Zahlen in Echtzeit · Stand {new Date(data.stand).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr
          <button data-testid="creator-stats-refresh" onClick={load} className="ml-2 inline-flex items-center gap-1 text-terra hover:underline">
            <RefreshCw className="h-3.5 w-3.5" /> aktualisieren
          </button>
        </p>

        <div className="mt-5 rounded-2xl bg-sand-800 text-cream-50 p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-cream-50/70">Dein Link · Code {data.code} (−{data.rabatt_pct} %)</p>
            <p className="font-semibold">{data.link.replace("https://", "")}</p>
          </div>
          <button
            data-testid="creator-stats-copy-link"
            onClick={async () => { await navigator.clipboard.writeText(data.link); toast.success("Link kopiert!"); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-terra px-4 py-2 text-sm font-semibold"
          >
            <Copy className="h-4 w-4" /> Kopieren
          </button>
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Heute</h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          <Stat icon={MousePointerClick} label="Klicks" value={data.heute.clicks} sub={`gestern: ${data.gestern.clicks}`} testid="stat-today-clicks" />
          <Stat icon={CalendarCheck} label="Buchungen" value={data.heute.bookings} sub={`gestern: ${data.gestern.bookings}`} testid="stat-today-bookings" />
          <Stat icon={Euro} label="Umsatz" value={eur(data.heute.revenue)} sub={`gestern: ${eur(data.gestern.revenue)}`} testid="stat-today-revenue" />
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Deine Provisions-Stufen</h2>
        <div className="mt-3 rounded-2xl bg-white border border-cream-300 p-5" data-testid="creator-stats-tiers">
          <div className="flex flex-wrap gap-2">
            {data.stufen.tiers.map((t) => (
              <span
                key={t.ab}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold ${
                  data.stufen.aktuelle_pct >= t.pct ? "bg-terra text-cream-50" : "bg-cream-200 text-sand-600"
                }`}
              >
                {t.pct} % {t.ab > 1 ? `ab ${t.ab}. Buchung` : "Start"}
              </span>
            ))}
          </div>
          {data.stufen.naechste ? (
            <>
              <div className="mt-4 h-2.5 rounded-full bg-cream-200 overflow-hidden">
                <div
                  className="h-full bg-terra rounded-full transition-all"
                  style={{ width: `${Math.min(100, (data.stufen.buchungen / data.stufen.naechste.ab) * 100)}%` }}
                />
              </div>
              <p className="mt-2 text-sm text-sand-700">
                <strong>{data.stufen.buchungen}</strong> von {data.stufen.naechste.ab} Buchungen — noch{" "}
                <strong className="text-terra">{data.stufen.naechste.fehlen}</strong> bis zu{" "}
                <strong className="text-terra">{data.stufen.naechste.pct} % Provision</strong> pro Buchung 🚀
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm font-semibold text-terra">Höchste Stufe erreicht — {data.stufen.aktuelle_pct} % auf jede Buchung! 🏆</p>
          )}
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Gesamt</h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={MousePointerClick} label="Klicks" value={data.gesamt.clicks} testid="stat-total-clicks" />
          <Stat icon={CalendarCheck} label="Buchungen" value={data.gesamt.bookings} sub={`${data.gesamt.leads} Beratungsanfragen`} testid="stat-total-bookings" />
          <Stat icon={Euro} label="Umsatz" value={eur(data.gesamt.revenue)} testid="stat-total-revenue" />
          <Stat icon={Wallet} label="Deine Provision" value={eur(data.gesamt.provision)} sub={`aktuell ${data.stufen.aktuelle_pct} % pro Buchung`} testid="stat-total-provision" />
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Was drin ist für dich</h2>
        <div className="mt-3 rounded-2xl bg-white border border-cream-300 p-5" data-testid="creator-stats-potenzial">
          <p className="text-sm text-sand-600">
            Ø Buchungswert: <strong className="text-sand-800">{eur(data.potenzial.basis)}</strong>{" "}
            <span className="text-xs text-sand-400">({data.potenzial.basis_quelle})</span>
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {data.potenzial.pro_buchung.map((p) => (
              <div key={p.pct} className="rounded-xl bg-cream-100 p-3 text-center">
                <p className="text-xs text-sand-500">{p.pct} % {p.ab > 1 ? `ab ${p.ab}.` : "Start"}</p>
                <p className="mt-1 font-heading font-bold text-terra">{eur(p.betrag)}</p>
                <p className="text-[10px] text-sand-400">pro Buchung</p>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1.5">
            {data.potenzial.beispiele.map((b) => (
              <p key={b.buchungen} className="text-sm text-sand-700 flex justify-between">
                <span>{b.buchungen} Buchungen</span>
                <strong className="text-terra">≈ {eur(b.provision)}</strong>
              </p>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-sand-400">Hochrechnung inkl. Stufen — keine Garantie, aber dein Ziel. 😉</p>
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Letzte 14 Tage · Klicks</h2>
        <div className="mt-3 rounded-2xl bg-white border border-cream-300 p-5">
          <div className="flex items-end gap-1.5 h-28" data-testid="creator-stats-chart">
            {data.verlauf.map((d) => (
              <div key={d.tag} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className={`w-full rounded-t ${d.bookings > 0 ? "bg-terra" : "bg-sand-300"}`}
                  style={{ height: `${Math.max(4, (d.clicks / maxClicks) * 100)}%` }}
                  title={`${d.tag}: ${d.clicks} Klicks, ${d.bookings} Buchungen`}
                />
                <span className="text-[9px] text-sand-400">{d.tag.slice(8)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-sand-500">Terra-Balken = Tage mit Buchungen.</p>
        </div>

        <h2 className="mt-8 font-heading font-bold text-lg text-sand-800">Letzte Buchungen</h2>
        <div className="mt-3 rounded-2xl bg-white border border-cream-300 divide-y divide-cream-200" data-testid="creator-stats-bookings">
          {data.letzte_buchungen.length === 0 ? (
            <p className="p-5 text-sm text-sand-500">Noch keine Buchungen — deine nächste Story ändert das. 💪</p>
          ) : (
            data.letzte_buchungen.map((b, i) => (
              <div key={i} className="flex items-center justify-between p-4 text-sm">
                <div>
                  <p className="font-semibold text-sand-800">{b.kurs}</p>
                  <p className="text-xs text-sand-500">{b.datum && new Date(b.datum).toLocaleDateString("de-DE")}</p>
                </div>
                <div className="text-right">
                  <p className="text-sand-700">{eur(b.betrag)}</p>
                  <p className="text-xs font-semibold text-terra">+{eur(b.provision)} für dich ({b.provision_pct} %)</p>
                </div>
              </div>
            ))
          )}
        </div>

        <p className="mt-6 text-xs text-sand-400">
          Aktualisiert sich automatisch jede Minute. Provision wird auf bezahlte Buchungen berechnet und monatlich abgerechnet.
        </p>
      </div>
    </div>
  );
}
